import AutoHeight from 'embla-carousel-auto-height'
import useEmblaCarousel from 'embla-carousel-react'
import qs from 'qs'
import { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'

import { useGetMultiplePropertiesEmissions } from '@/api/co2/hooks'
import { useDictionaryData } from '@/components/shared/DictionaryProvider/hooks'
import { useOffice } from '@/hooks/useOffice'
import { useUser } from '@/hooks/useUser'
import { useMixpanel } from '@/services/MixpanelService'

import { parseHorIdFromHtml } from './helpers'

import { get, put } from '../../api/api'
import { ENDPOINTS } from '../../api/endpoints'

import type { GETHorSearchIncludedResponse, Hor } from '../../api/types/hor'
import type { GetEmailInfoPayload } from '../../api/types/sendToEmployee'

type ShortlistSendForm = {
  approvedShortlists: (number | string)[]
}

type ToggleApprovalPayload = {
  id?: string
  hash?: string
  rmcApproved: string
}

export const useGetShortlist = () => {
  const { dictionary, isLoading: isDictionaryLoading } = useDictionaryData()
  const [emblaRef, emblaApi] = useEmblaCarousel({}, [AutoHeight()])

  const [emailHorId, setEmailHorId] = useState<number | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [searchData, setSearchData] =
    useState<GETHorSearchIncludedResponse | null>(null)
  const [horData, setHorData] = useState<Hor | null>(null)
  const [selectedPropertyIndex, setSelectedPropertyIndex] = useState<number>(0)
  const [wasShortlistSent, setWasShortlistSent] = useState<boolean>(false)
  const [isUpdatingApprovals, setIsUpdatingApprovals] = useState<boolean>(false)
  const [isSendToEmployeeModalOpen, setIsSendToEmployeeModalOpen] =
    useState<boolean>(false)
  const [isSendingMail, setIsSendingMail] = useState<boolean>(false)
  const [mailError, setMailError] = useState<string | null>(null)
  const [shouldSendEvent, setShouldSendEvent] = useState(false)
  const mixpanel = useMixpanel()

  const { horId: horIdFromUrl } = qs.parse(window.location.search, {
    ignoreQueryPrefix: true,
  })
  const horId = emailHorId || horIdFromUrl

  const { data: user, isLoading: isUserLoading } = useUser()
  const userRole = user?.rmcRole ? 'rmc' : 'admin'
  const co2Data = useGetMultiplePropertiesEmissions(searchData?.propertyList)

  const isLoading =
    (!error && !horId) ||
    !searchData ||
    !horData ||
    isSendingMail ||
    isDictionaryLoading

  useEffect(() => {
    if (!horId && !isLoading) {
      setError('No Housing Request ID found')
    } else if (!user && !isUserLoading) {
      setError('User not found')
    } else {
      setError(null)
    }
    if (horId && !!user) {
      get(
        `${process.env.API_BASE_URL}${ENDPOINTS.FETCH_PROPERTIES(horId, userRole)}`
      )
        .then(setSearchData)
        .catch((error) => {
          setError(error.message)
        })
      get(`${process.env.API_BASE_URL}${ENDPOINTS.FETCH_HOR(horId, userRole)}`)
        .then(setHorData)
        .catch((error) => {
          setError(error.message)
        })
    }
  }, [horId, isLoading, isUserLoading, user])

  const updateEmailHorId = (emailBody: string) => {
    const { id, wasFound } = parseHorIdFromHtml(emailBody)
    if (wasFound) {
      setEmailHorId(id)
    }
  }

  useOffice({
    onItemBodyChangedCallback: updateEmailHorId,
  })

  useEffect(() => {
    if (searchData) {
      const approvedProperties = searchData.propertyList
        ?.filter((property) => property.customFields['rmc-approved'] === '1')
        ?.map((property) => property.id)
      methods.setValue('approvedShortlists', approvedProperties)
      setIsUpdatingApprovals(false)
    }
  }, [searchData])

  const methods = useForm<ShortlistSendForm>({
    defaultValues: {
      approvedShortlists: [],
    },
  })

  const onApprovalToggle = (propertyId: number) => {
    const currentlyApproved = methods.getValues('approvedShortlists')

    const newList = currentlyApproved.includes(propertyId.toString())
      ? currentlyApproved.filter((id) => id !== propertyId)
      : [...currentlyApproved, propertyId]

    const hasBeenUnapproved = !newList.includes(propertyId.toString())
    if (hasBeenUnapproved && mixpanel) {
      mixpanel.track('Outlook - Unapproved Property', {
        propertyId,
        horId,
      })
    }

    methods.setValue('approvedShortlists', newList)

    const putPropertyList = searchData?.propertyList?.map((property) => {
      const payloadPerProperty: ToggleApprovalPayload = {
        rmcApproved: newList.includes(property.id.toString()) ? '1' : '0',
      }
      if (userRole === 'rmc') {
        payloadPerProperty.hash = String(property.id)
      } else if (userRole === 'admin') {
        payloadPerProperty.id = property.id
      }
      return payloadPerProperty
    })

    put(
      `${process.env.API_BASE_URL}${ENDPOINTS.UPDATE_PROPERTIES(horId, userRole)}`,
      {
        propertyList: putPropertyList,
      }
    )
  }
  const approvedProperties = methods.watch('approvedShortlists')

  const onSubmitSendToEmployeeModal = (data: GetEmailInfoPayload) => {
    setMailError(null)
    setError(null)
    setIsSendingMail(true)
    if (approvedProperties.length === 0) {
      setError('Please select at least one property')
      setIsSendingMail(false)
    } else {
      put(
        `${process.env.API_BASE_URL}${ENDPOINTS.SEND_TO_EMPLOYEE(horId, userRole)}`,
        data
      )
        .then(() => {
          setShouldSendEvent(true)
        })
        .finally(() => {
          setIsSendingMail(false)
          setIsSendToEmployeeModalOpen(false)
          setWasShortlistSent(true)
        })
    }
  }

  useEffect(() => {
    if (mixpanel && shouldSendEvent) {
      mixpanel.track(
        `Outlook - Sent Options to ${dictionary.employee}`,
        {
          horId,
        },
        { send_immediately: true }
      )
      setShouldSendEvent(false)
    }
  }, [mixpanel, shouldSendEvent])

  const onCloseSendToEmployeeModal = () => {
    setIsSendToEmployeeModalOpen(false)
  }
  const onClickSendToEmployeeButton = () => {
    setIsSendToEmployeeModalOpen(true)
  }

  const onPropertyIndexChange = (newIndex: number) => {
    setSelectedPropertyIndex(newIndex)
    emblaApi?.scrollTo(newIndex)
  }

  useEffect(() => {
    if (!emblaApi) return

    const onChangeIndex = () => {
      setSelectedPropertyIndex(emblaApi.selectedScrollSnap())
    }

    emblaApi.on('select', onChangeIndex)

    return () => {
      emblaApi.off('select', onChangeIndex)
    }
  }, [emblaApi])

  const onGoBackFromSuccessfulEmail = () => {
    setWasShortlistSent(false)
  }

  return {
    approvedProperties,
    emblaRef,
    error,
    hasError: !!error,
    hor: horData,
    isLoading,
    methods,
    nProperties: searchData?.propertyList?.length,
    onApprovalToggle,
    onPropertyIndexChange,
    properties: searchData?.propertyList,
    selectedPropertyIndex,
    wasShortlistSent,
    isUpdatingApprovals,
    isSendToEmployeeModalOpen,
    onSubmitSendToEmployeeModal,
    onCloseSendToEmployeeModal,
    onClickSendToEmployeeButton,
    isSendingMail,
    mailError,
    onGoBackFromSuccessfulEmail,
    co2Data,
    dictionary,
  }
}
